/**
 * Framework-free conductor for the iframe's welcome/audio-activation ceremony
 * (docs/decisions/0039-ai-audio-mic-enablement.md). Owns the parent-protocol
 * choreography that the old React standalone entry expressed as React state:
 *
 *   ENTER_WELCOME → overlay hidden while the gate resolves →
 *   gate verdict (room-confirmed canPlayAudio is the authority; the pre-room
 *   probe fires only via the fallback timer) → reveal + WELCOME_READY{gated} →
 *   CTA tap burns the in-iframe gesture (startAudio + WELCOME_PROCEED) →
 *   EXIT_WELCOME cross-fades out and lands in 'ingame'.
 *
 * The conductor owns phase/gate/ceremony only. It does NOT derive the widget's
 * display mode — the host combines `view.audioBlocked` with its own transport
 * signal via deriveWelcomeMode(), and owns how the view maps to widget mounts.
 *
 * Lives beside the standalone entry (not core/logic): the ceremony is the
 * iframe↔parent contract, and this module may use the app-only flow log.
 */

import { flowStart, flowLog } from '../welcome-flow-log.js';

/** How long to wait for the room's authoritative canPlayAudio before falling back to the
 * conservative pre-room probe to decide the welcome gate. Well under the game's 15–20s watchdog. */
export const WELCOME_GATE_FALLBACK_MS = 2000;

/** Cross-fade duration for the welcome → regular-widget hand-off (overlay fades out, room fades in). */
export const WELCOME_EXIT_FADE_MS = 300;

export type WelcomePhase = 'none' | 'welcome' | 'ingame';

export interface WelcomeView {
  phase: WelcomePhase;
  /** The gate resolved — reveal the overlay in its final state (teach or CTA), never mid-flip. */
  resolved: boolean;
  /** EXIT_WELCOME received — overlay is cross-fading out (phase flips to 'ingame' after the fade). */
  exiting: boolean;
  /** Gate verdict: audible autoplay is blocked → the CTA must collect an in-iframe tap. */
  audioBlocked: boolean;
}

/** Outbound ceremony messages for the parent game. */
export type WelcomeOutboundMessage =
  | { type: 'WELCOME_READY'; payload: { gated: boolean } }
  | { type: 'WELCOME_PROCEED'; payload: Record<string, never> };

export interface WelcomeConductorDeps {
  /** Pre-room autoplay probe (conservative estimate; used only by the fallback timer). */
  probeAudioBlocked(): Promise<boolean>;
  /** Post a ceremony message to the parent game. */
  post(message: WelcomeOutboundMessage): void;
  /** Burn the CTA tap's user activation on the audio unlock (host wires to sdk.startAudio()). */
  startAudio(): void;
  /** View change notification (fires only on actual change). */
  onChange(view: WelcomeView): void;
  /** Test seams — production uses the module defaults. */
  gateFallbackMs?: number;
  exitFadeMs?: number;
}

export interface WelcomeConductor {
  getView(): WelcomeView;
  /** ENTER_WELCOME from the parent: hide the overlay, start the probe, arm the fallback. */
  enterWelcome(): void;
  /** EXIT_WELCOME from the parent: cross-fade out, then land in 'ingame'. */
  exitWelcome(): void;
  /** The player tapped the gated CTA inside the iframe. */
  ctaTap(): void;
  /** The room reported its REAL audio-playable state (authoritative; welcome phase only). */
  audioPlayableChanged(canPlay: boolean): void;
  destroy(): void;
}

export function createWelcomeConductor(deps: WelcomeConductorDeps): WelcomeConductor {
  const gateFallbackMs = deps.gateFallbackMs ?? WELCOME_GATE_FALLBACK_MS;
  const exitFadeMs = deps.exitFadeMs ?? WELCOME_EXIT_FADE_MS;

  let view: WelcomeView = {
    phase: 'none',
    resolved: false,
    exiting: false,
    audioBlocked: false,
  };

  // Last gate verdict actually emitted — WELCOME_READY fires only when it flips,
  // so repeated room reports don't spam the game's auto-dismiss arming.
  let lastGated: boolean | null = null;
  // Pre-room probe estimate; DEFAULT-TO-BLOCKED until the probe says otherwise.
  let probeBlocked = true;
  let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
  let exitTimer: ReturnType<typeof setTimeout> | null = null;

  const patch = (partial: Partial<WelcomeView>): void => {
    const changed = (Object.keys(partial) as Array<keyof WelcomeView>).some(
      (key) => partial[key] !== view[key]
    );
    if (!changed) return;
    view = { ...view, ...partial };
    deps.onChange(view);
  };

  const clearFallback = (): void => {
    if (fallbackTimer !== null) {
      clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }
  };

  const clearExit = (): void => {
    if (exitTimer !== null) {
      clearTimeout(exitTimer);
      exitTimer = null;
    }
  };

  /** Resolve the gate to a definitive verdict: reveal the overlay and tell the game —
   * but WELCOME_READY only when the verdict actually flips. The room is the authority;
   * any resolution cancels the pending probe fallback. */
  const resolveGate = (gated: boolean, source: string): void => {
    clearFallback();
    const noop = lastGated === gated;
    flowLog(
      `GATE RESOLVED via=${source} gated=${gated}` +
        (noop ? ' (already resolved — no WELCOME_READY)' : ' → reveal widget + post WELCOME_READY')
    );
    if (noop) {
      patch({ resolved: true });
      return;
    }
    lastGated = gated;
    patch({ resolved: true, audioBlocked: gated });
    deps.post({ type: 'WELCOME_READY', payload: { gated } });
  };

  return {
    getView: () => view,

    enterWelcome(): void {
      flowStart('ENTER_WELCOME received — widget hidden, probe started, fallback armed');
      clearExit();
      lastGated = null;
      // Optimistic: default to the teaching widget — do NOT flash the "Tap to start" CTA
      // on the conservative pre-room probe. The CTA is raised only when the room CONFIRMS
      // audio is blocked, or via the probe fallback if the room stays silent. No
      // WELCOME_READY here — only on resolution.
      patch({ phase: 'welcome', resolved: false, exiting: false, audioBlocked: false });
      void deps.probeAudioBlocked().then((blocked) => {
        probeBlocked = blocked;
        flowLog(
          `probe resolved: blocked=${blocked} (pre-room estimate; used only if room is slow)`
        );
      });
      clearFallback();
      fallbackTimer = setTimeout(() => {
        fallbackTimer = null;
        flowLog(
          `${gateFallbackMs}ms FALLBACK fired — room never reported; resolving via probe (blocked=${probeBlocked})`
        );
        resolveGate(probeBlocked, `probe-fallback(${gateFallbackMs}ms)`);
      }, gateFallbackMs);
    },

    exitWelcome(): void {
      flowLog('EXIT_WELCOME received → cross-fade out');
      clearFallback();
      // Keep the overlay mounted (phase stays 'welcome') but mark exiting so it fades out
      // while the regular widget fades in; finalize after the fade.
      patch({ exiting: true });
      clearExit();
      exitTimer = setTimeout(() => {
        exitTimer = null;
        patch({ phase: 'ingame', resolved: false, exiting: false });
      }, exitFadeMs);
    },

    ctaTap(): void {
      flowLog('welcome CTA tapped → startAudio (in-iframe gesture) + WELCOME_PROCEED');
      deps.startAudio();
      deps.post({ type: 'WELCOME_PROCEED', payload: {} });
    },

    audioPlayableChanged(canPlay: boolean): void {
      if (view.phase !== 'welcome') {
        return;
      }
      flowLog(`room reported audioPlayable=${canPlay} (AudioContext-confirmed) → resolve`);
      resolveGate(!canPlay, 'room');
    },

    destroy(): void {
      clearFallback();
      clearExit();
    },
  };
}
